import { clientMoveIn } from "../../lib/clientMoveIn";
// src/components/clients/UploadLeaseModal.tsx

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  Send as SendIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import type { Client, Agent } from "./types";
import { sendLeaseEmail } from "../../services/emailService";
import { supabase } from "../../lib/supabase";
import { isMobileDevice } from "./helpers";

interface UploadLeaseModalProps {
  open: boolean;
  onClose: () => void;
  client: Client;
  onSuccess?: () => void;
  currentAgent?: Agent | null;
  isDemo?: boolean;
}

const RECIPIENT_OPTIONS = [
  {
    email: "stephanie.klan@realmccoy.com",
    label: "Stephanie Klan (Real McCoy)",
  },
  {
    email: "grant@locatenowatl.com",
    label: "Grant Harney (test)",
  },
];

const MAX_FILE_SIZE_MB = 25;

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const buildSubject = (client: Client) =>
  `Lease/Proof — ${client.first_name} ${client.last_name}`;

const buildBody = (client: Client, agentName: string, recipientFirstName: string) => {
  const moveIn = clientMoveIn(client) ? `\nMove-in: ${clientMoveIn(client)}` : "";
  return `Hi ${recipientFirstName},

Attached is the lease/proof of transaction for ${client.first_name} ${client.last_name}.${moveIn}

Let me know if you need anything else.

Thanks,
${agentName}`;
};

const bodyToHtml = (text: string) => {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #1f2937;">${escaped
    .split("\n")
    .map((line) => (line.trim() === "" ? "<br/>" : `<p style="margin: 0 0 8px 0;">${line}</p>`))
    .join("")}</div>`;
};

const UploadLeaseModal: React.FC<UploadLeaseModalProps> = ({
  open,
  onClose,
  client,
  onSuccess,
  currentAgent,
  isDemo = false,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = isMobileDevice();

  const agentName =
    currentAgent?.full_name || "LocateNow";
  const defaultRecipient = RECIPIENT_OPTIONS[0];
  const recipientFirstName = (label: string) => label.split(" ")[0] || "there";

  const [recipientEmail, setRecipientEmail] = useState<string>(defaultRecipient.email);
  const [subject, setSubject] = useState<string>(buildSubject(client));
  const [body, setBody] = useState<string>(
    buildBody(client, agentName, recipientFirstName(defaultRecipient.label))
  );
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reset form whenever modal opens for a new client
  useEffect(() => {
    if (open) {
      setRecipientEmail(defaultRecipient.email);
      setSubject(buildSubject(client));
      setBody(
        buildBody(client, agentName, recipientFirstName(defaultRecipient.label))
      );
      setFile(null);
      setError(null);
      setSuccessMessage(null);
      setSending(false);
    }
  }, [open, client, agentName, defaultRecipient.email, defaultRecipient.label]);

  // When recipient changes, refresh body greeting
  const handleRecipientChange = (email: string) => {
    setRecipientEmail(email);
    const opt = RECIPIENT_OPTIONS.find((o) => o.email === email);
    if (opt) {
      setBody(buildBody(client, agentName, recipientFirstName(opt.label)));
    }
  };

  const handleFileSelect = (selected: File | null) => {
    setError(null);
    if (!selected) {
      setFile(null);
      return;
    }
    const sizeMb = selected.size / (1024 * 1024);
    if (sizeMb > MAX_FILE_SIZE_MB) {
      setError(`File too large (${sizeMb.toFixed(1)} MB). Max ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    setFile(selected);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0] || null;
    handleFileSelect(dropped);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleSend = async () => {
    if (!file) {
      setError("Please attach a file before sending.");
      return;
    }
    setSending(true);
    setError(null);

    try {
      const result = await sendLeaseEmail({
        to: recipientEmail,
        subject,
        html_body: bodyToHtml(body),
        from_name: agentName,
        file,
        source_type: "lease",
        client_id: client.id,
      });

      if (!result.success) {
        setError(result.error || "Failed to send email.");
        setSending(false);
        return;
      }

      // Log the activity
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          await supabase.from("activities").insert({
            user_id: user.id,
            activity_type: "lease_uploaded",
            entity_type: "client",
            entity_id: client.id,
            metadata: {
              client_name: `${client.first_name} ${client.last_name}`,
              recipient_email: recipientEmail,
              file_name: file.name,
              file_size: file.size,
              ...(result.tracking_id && { tracking_id: result.tracking_id }),
            },
          });
        }
      } catch (logErr) {
        console.error("Failed to log lease upload activity:", logErr);
      }

      setSuccessMessage("Lease emailed successfully!");
      setSending(false);

      // Auto-close after a short delay
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error("Lease upload error:", err);
      setError(err?.message || "Failed to send email.");
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <UploadIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Upload Lease
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {client.first_name} {client.last_name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={sending}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {successMessage ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            {successMessage}
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Recipient</InputLabel>
              <Select
                label="Recipient"
                value={recipientEmail}
                onChange={(e) => handleRecipientChange(e.target.value)}
                disabled={sending}
              >
                {RECIPIENT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.email} value={opt.email}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
            />

            <TextField
              fullWidth
              multiline
              minRows={5}
              maxRows={10}
              label="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
            />

            {/* File picker */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
              >
                Attachment
              </Typography>
              {file ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }}
                >
                  <FileIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      noWrap
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleFileSelect(null)}
                    disabled={sending}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    cursor: "pointer",
                    p: 3,
                    textAlign: "center",
                    border: `2px dashed ${
                      dragActive
                        ? theme.palette.primary.main
                        : theme.palette.divider
                    }`,
                    borderRadius: 2,
                    backgroundColor: dragActive
                      ? alpha(theme.palette.primary.main, 0.06)
                      : "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 32,
                      color: alpha(theme.palette.primary.main, 0.7),
                      mb: 0.5,
                    }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {isMobile ? "Tap to attach" : "Drop file here or click to browse"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF or image · max {MAX_FILE_SIZE_MB} MB
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      handleFileSelect(e.target.files?.[0] || null)
                    }
                  />
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      {!successMessage && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !file}
            startIcon={
              sending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UploadLeaseModal;
